// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: sends files (with an optional line of text) to a chat from outside its composer, like
// any upload: the files go up with the message and the chat shows it at once while they do.
// The chat's own composer and its picked files are left alone.

import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import {Message} from '@app/features/messaging/models/MessagingMessage';
import {UploadingAttachment} from '@app/features/messaging/models/UploadingAttachment';
import {CloudUpload} from '@app/features/messaging/upload/CloudUpload';
import Users from '@app/features/user/state/Users';
import {MessageStates, MessageTypes} from '@fluxer/constants/src/ChannelConstants';
import type {Message as WireMessage} from '@fluxer/schema/src/domains/message/MessageResponseSchemas';
import * as SnowflakeUtils from '@fluxer/snowflake/src/SnowflakeUtils';

export async function sendFilesMessage(
	channelId: string,
	files: ReadonlyArray<File>,
	content = '',
	nonce: string = SnowflakeUtils.fromTimestamp(Date.now()),
): Promise<WireMessage | null> {
	const currentUser = Users.getCurrentUser();
	if (!currentUser) throw new Error('Current user missing');
	const uploads = await CloudUpload.createAndStartUploads(channelId, [...files]);
	CloudUpload.claimAttachmentsForMessage(channelId, nonce, uploads, {content});
	const attachments = files.map((file) =>
		UploadingAttachment.fromDescriptor({
			filename: file.name,
			title: file.name,
			size: file.size,
			contentType: file.type,
		}).toJSON(),
	);
	const message = new Message({
		id: nonce,
		channel_id: channelId,
		author: currentUser.toJSON(),
		type: MessageTypes.DEFAULT,
		flags: 0,
		pinned: false,
		mention_everyone: false,
		content,
		timestamp: new Date().toISOString(),
		mentions: [],
		state: MessageStates.SENDING,
		nonce,
		attachments,
	});
	MessageCommands.createOptimistic(channelId, {...message.toJSON(), attachments});
	return MessageCommands.send(channelId, {content, nonce, hasAttachments: true});
}
